import React from "react";
import { Button } from "@/components/ui/button";
import { FileText, Trash2 } from "lucide-react";
import { Input } from "@/components/ui/input";
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogFooter,
} from "@/components/ui/dialog";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "@/components/ui/select";
import Toast from "@/pages/portal/components/Toast";

const months = [
    { label: "January", value: "01" },
    { label: "February", value: "02" },
    { label: "March", value: "03" },
    { label: "April", value: "04" },
    { label: "May", value: "05" },
    { label: "June", value: "06" },
    { label: "July", value: "07" },
    { label: "August", value: "08" },
    { label: "September", value: "09" },
    { label: "October", value: "10" },
    { label: "November", value: "11" },
    { label: "December", value: "12" },
];

const currentYear = new Date().getFullYear();
const years = Array.from({ length: (currentYear + 2) - (currentYear - 50) + 1 }, (_, i) => {
    const y = currentYear + 2 - i;
    return { label: String(y), value: String(y) };
});

interface ExamModalProps {
    isOpen: boolean;
    onOpenChange: (open: boolean) => void;
    editingItem: any;
    onDelete: () => void;
    onSave: (data: any) => Promise<void> | void;
    examTypes?: Array<{ label: string; value: string }>;
}

export default function ExamModal({
    isOpen,
    onOpenChange,
    editingItem,
    onDelete,
    onSave,
    examTypes = [],
}: ExamModalProps) {
    const [examName, setExamName] = React.useState(editingItem?.name || "");
    const [score, setScore] = React.useState(editingItem?.score || "");
    const [examMonth, setExamMonth] = React.useState(editingItem?.examMonth || "");
    const [examYear, setExamYear] = React.useState(editingItem?.examYear || "");
    const [errors, setErrors] = React.useState<Record<string, string>>({});
    const [isSaving, setIsSaving] = React.useState(false);
    const [saveError, setSaveError] = React.useState<string | null>(null);
    const [showValidationToast, setShowValidationToast] = React.useState(false);

    React.useEffect(() => {
        if (isOpen) {
            setExamName(editingItem?.name || "");
            setScore(editingItem?.score || "");
            setExamMonth(editingItem?.examMonth || "");
            setExamYear(editingItem?.examYear || "");
            setErrors({});
            setShowValidationToast(false);
        }
    }, [isOpen, editingItem]);

    const handleSave = async () => {
        const newErrors: Record<string, string> = {};
        if (!examName) newErrors.examName = "Exam name is required.";
        if (!score.trim()) newErrors.score = "Score is required.";
        if (!examMonth) newErrors.examMonth = "Exam month is required.";
        if (!examYear) newErrors.examYear = "Exam year is required.";
        if (Object.keys(newErrors).length > 0) {
            setErrors(newErrors);
            setShowValidationToast(true);
            const fieldOrder = ["examName", "score", "examMonth", "examYear"];
            const firstErrorField = fieldOrder.find((f) => newErrors[f]);
            if (firstErrorField) {
                setTimeout(() => {
                    document.getElementById(`exam-field-${firstErrorField}`)?.scrollIntoView({ behavior: "smooth", block: "nearest" });
                }, 0);
            }
            return;
        }
        setIsSaving(true);
        setSaveError(null);
        try {
            await onSave({ id: editingItem?.id, name: examName, type: examName, score, examMonth, examYear, date: `${examMonth}/${examYear}` });
            setErrors({});
            onOpenChange(false);
        } catch (error: any) {
            const msg = error?.response?.data?.detail || error?.message || "Failed to save. Please try again.";
            setSaveError(msg);
        } finally {
            setIsSaving(false);
        }
    };

    const fieldCls = (error?: string) =>
        `h-11 rounded-lg border bg-white text-sm font-normal focus-visible:ring-2 focus-visible:ring-[#001B44]/15 focus-visible:border-[#001B44] transition-colors ${
            error ? "border-red-400 focus-visible:ring-red-200/60" : "border-slate-800"
        }`;
    const triggerCls = (error?: string) =>
        `h-11 rounded-lg border bg-white text-sm font-normal focus:ring-2 focus:ring-[#001B44]/15 focus:border-[#001B44] transition-colors ${
            error ? "border-red-400" : "border-slate-800"
        }`;

    const SectionHeading = ({ children }: { children: React.ReactNode }) => (
        <div className="flex items-center gap-3 pb-1">
            <span className="text-[11px] font-semibold text-slate-400 uppercase tracking-widest whitespace-nowrap">
                {children}
            </span>
            <div className="flex-1 h-px bg-slate-100" />
        </div>
    );

    const FieldLabel = ({ htmlFor, children, required }: { htmlFor?: string; children: React.ReactNode; required?: boolean }) => (
        <label htmlFor={htmlFor} className="block text-sm font-medium text-slate-700 mb-1.5">
            {children}
            {required && <span className="text-rose-400 text-xs ml-1">*</span>}
        </label>
    );

    return (
        <Dialog open={isOpen} onOpenChange={onOpenChange}>
            <DialogContent className="sm:max-w-[560px] max-h-[90vh] rounded-2xl p-0 border-none shadow-2xl flex flex-col bg-white overflow-hidden">
                {showValidationToast && (
                    <Toast
                        message="Please fill in all required fields."
                        type="error"
                        mode="absolute"
                        onClose={() => setShowValidationToast(false)}
                        duration={4000}
                    />
                )}

                {/* ── Header ───────────────────────────────────────────── */}
                <DialogHeader className="px-6 pt-6 pb-5 shrink-0 border-b border-slate-100">
                    <div className="flex items-center gap-4">
                        <div className="w-11 h-11 rounded-xl bg-[#001B44]/[0.06] flex items-center justify-center text-[#001B44] shrink-0">
                            <FileText className="w-5 h-5" />
                        </div>
                        <div>
                            <DialogTitle className="text-[17px] font-bold text-slate-900 leading-tight">
                                {editingItem ? "Edit Exam" : "Add Exam"}
                            </DialogTitle>
                            <p className="text-sm text-slate-400 mt-0.5">
                                {editingItem
                                    ? "Update your proficiency exam details."
                                    : "Add a language or proficiency exam result."}
                            </p>
                        </div>
                    </div>
                </DialogHeader>

                {/* ── Scrollable body ───────────────────────────────────── */}
                <div className="flex-1 overflow-y-auto px-6 py-6 space-y-7">

                    {/* Section 1 — Exam Details */}
                    <div className="space-y-4">
                        <SectionHeading>Exam Details</SectionHeading>

                        <div id="exam-field-examName">
                            <FieldLabel required>Exam Name</FieldLabel>
                            <Select value={examName} onValueChange={(v) => { setExamName(v); setErrors((p) => ({ ...p, examName: "" })); }}>
                                <SelectTrigger className={triggerCls(errors.examName)}>
                                    <SelectValue placeholder="Select exam type" />
                                </SelectTrigger>
                                <SelectContent className="max-h-[200px]">
                                    {examTypes.map((exam) => (
                                        <SelectItem key={exam.value} value={exam.value}>{exam.label}</SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                            {errors.examName && <p className="text-xs text-red-500 mt-1">{errors.examName}</p>}
                        </div>

                        <div id="exam-field-score">
                            <FieldLabel required>Score</FieldLabel>
                            <Input
                                value={score}
                                onChange={(e) => { setScore(e.target.value); setErrors((p) => ({ ...p, score: "" })); }}
                                placeholder="e.g. 7.5"
                                className={fieldCls(errors.score)}
                            />
                            {errors.score && <p className="text-xs text-red-500 mt-1">{errors.score}</p>}
                        </div>
                    </div>

                    {/* Section 2 — Exam Date */}
                    <div className="space-y-4">
                        <SectionHeading>Exam Date</SectionHeading>

                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                            <div id="exam-field-examMonth">
                                <FieldLabel required>Month</FieldLabel>
                                <Select value={examMonth} onValueChange={(v) => { setExamMonth(v); setErrors((p) => ({ ...p, examMonth: "" })); }}>
                                    <SelectTrigger className={triggerCls(errors.examMonth)}>
                                        <SelectValue placeholder="Select month" />
                                    </SelectTrigger>
                                    <SelectContent className="max-h-[200px]">
                                        {months.map((m) => (
                                            <SelectItem key={m.value} value={m.value}>{m.label}</SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                                {errors.examMonth && <p className="text-xs text-red-500 mt-1">{errors.examMonth}</p>}
                            </div>
                            <div id="exam-field-examYear">
                                <FieldLabel required>Year</FieldLabel>
                                <Select value={examYear} onValueChange={(v) => { setExamYear(v); setErrors((p) => ({ ...p, examYear: "" })); }}>
                                    <SelectTrigger className={triggerCls(errors.examYear)}>
                                        <SelectValue placeholder="Select year" />
                                    </SelectTrigger>
                                    <SelectContent className="max-h-[200px]">
                                        {years.map((y) => (
                                            <SelectItem key={y.value} value={y.value}>{y.label}</SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                                {errors.examYear && <p className="text-xs text-red-500 mt-1">{errors.examYear}</p>}
                            </div>
                        </div>
                    </div>
                </div>

                {/* ── Footer ───────────────────────────────────────────── */}
                <DialogFooter className="px-6 pt-4 pb-5 bg-white border-t border-slate-100 shrink-0">
                    <div className="flex items-center justify-between w-full gap-3">
                        <div className="shrink-0">
                            {editingItem && (
                                <Button
                                    variant="ghost"
                                    onClick={onDelete}
                                    className="text-red-500 hover:text-red-600 hover:bg-red-50 font-medium px-3 h-10 text-sm"
                                >
                                    <Trash2 className="w-4 h-4 mr-1.5" />
                                    Delete
                                </Button>
                            )}
                        </div>
                        <div className="flex items-center gap-3">
                            {saveError && (
                                <p className="text-xs text-red-500">{saveError}</p>
                            )}
                            <Button
                                variant="ghost"
                                onClick={() => onOpenChange(false)}
                                className="text-slate-500 hover:text-slate-700 font-medium h-10 text-sm px-4"
                                disabled={isSaving}
                            >
                                Cancel
                            </Button>
                            <Button
                                onClick={handleSave}
                                disabled={isSaving}
                                className="rounded-xl bg-[#001B44] hover:bg-[#002a6e] text-white font-semibold px-6 h-10 text-sm shadow-sm transition-colors"
                            >
                                {isSaving ? "Saving…" : "Save Exam"}
                            </Button>
                        </div>
                    </div>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}
